import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CancelledError,
  DisallowedContentError,
  OffTopicInstructionsError,
  refusalMessage,
  useGenerateStoryMutation,
  useProgressLinesQuery,
} from "./queries";
import { useLoggedIn } from "./firebase";
import { lstr } from "./localization";
import { levelBadgeClasses } from "./levelColors";
import { ProgressOverlay } from "./ProgressOverlay";
import { EmailVerificationNotice } from "./EmailVerificationNotice";
import { PiCheckCircleFill, PiShuffle, PiSparkle } from "react-icons/pi";

export const levels = ["A1", "B1", "C1"] as const;
export const moods = [
  "romantic",
  "dark",
  "funny",
  "silly",
  "scary",
  "hopeful",
  "mysterious",
  "exciting",
  "charming",
  "thoughtful",
  "inspiring",
  "witty",
] as const;
export const topics = [
  "office",
  "family",
  "travel",
  "sports",
  "technology",
  "cooking",
  "fashion",
  "music",
  "science",
  "history",
  "nature",
  "movies",
] as const;

const levelCefr: Record<string, string> = {
  A1: "A1–A2",
  B1: "B1–B2",
  C1: "C1–C2",
};

const moodIcons: Record<string, string> = {
  romantic: "♡",
  dark: "◑",
  funny: "◡",
  silly: "✦",
  scary: "☾",
  hopeful: "☼",
  mysterious: "◌",
  exciting: "↗",
  charming: "✧",
  thoughtful: "⋯",
  inspiring: "✶",
  witty: "◆",
};

const MAX_SELECTIONS = 2;

// Mirrors generator.MaxInstructionsChars on the Go side; the backend
// re-validates the length and normalizes the text, so a client-side bypass
// is caught.
const MAX_INSTRUCTIONS_CHARS = 150;

// The level of the last generated story, preselected on the next visit.
const LAST_LEVEL_KEY = "lastGenerateLevel";

function loadLastLevel(): string {
  try {
    const level = localStorage.getItem(LAST_LEVEL_KEY);
    return level !== null && (levels as readonly string[]).includes(level)
      ? level
      : "";
  } catch (err) {
    console.error("Failed to read the last generate level", err);
    return "";
  }
}

function saveLastLevel(level: string) {
  try {
    localStorage.setItem(LAST_LEVEL_KEY, level);
  } catch (err) {
    console.error("Failed to store the last generate level", err);
  }
}

export function GenerateStoryView({ l, r }: { l: string; r: string }) {
  const [selectedLevel, setSelectedLevel] = useState<string>(loadLastLevel);
  const [selectedMoods, setSelectedMoods] = useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [instructions, setInstructions] = useState<string>("");

  const handleLevelSelect = (level: string) => setSelectedLevel(level);

  // Surprise rolls one mood and one topic, for when nothing comes to mind.
  const surprise = () => {
    const pick = <T,>(options: readonly T[]) =>
      options[Math.floor(Math.random() * options.length)];
    setSelectedMoods([pick(moods)]);
    setSelectedTopics([pick(topics)]);
  };

  // Counted in code points like the backend's rune count. Over-long input is
  // kept and flagged rather than cut off (no maxLength), so a paste is never
  // silently truncated.
  const instructionsLength = Array.from(instructions).length;
  const instructionsTooLong = instructionsLength > MAX_INSTRUCTIONS_CHARS;

  const navigate = useNavigate();
  const generate = useGenerateStoryMutation();
  const loggedIn = useLoggedIn();
  // One AbortController per in-flight request so the cancel button on the
  // overlay can tear down the fetch cleanly. The ref is reset just before
  // each new request so a previous cancellation never poisons a fresh run.
  const abortRef = useRef<AbortController | null>(null);

  const cancelGenerate = () => {
    abortRef.current?.abort();
  };

  // When the user cancels we get a CancelledError from the mutation - reset
  // it to idle so the user can immediately retry without the button stuck on
  // an error state.
  useEffect(() => {
    if (generate.isError && generate.error instanceof CancelledError) {
      generate.reset();
    }
  }, [generate.isError, generate.error, generate]);

  // At MAX_SELECTIONS the remaining options are disabled (see the option
  // grids) rather than silently swapping out an earlier choice, which may be
  // scrolled out of view.
  const toggleFromList = (
    item: string,
    list: string[],
    setList: (next: string[]) => void,
  ) => {
    if (list.includes(item)) {
      setList(list.filter((i) => i !== item));
    } else if (list.length < MAX_SELECTIONS) {
      setList([...list, item]);
    }
  };

  const handleGenerate = async () => {
    if (!loggedIn) {
      navigate("/login");
      return;
    }
    if (!selectedLevel || instructionsTooLong) return;
    // Retrying from the error state is allowed; only a request in flight
    // blocks a new one.
    if (generate.isPending) return;
    saveLastLevel(selectedLevel);
    abortRef.current = new AbortController();
    generate.mutate({
      l: l,
      r: r,
      level: selectedLevel,
      moods: selectedMoods,
      topics: selectedTopics,
      instructions: instructions.trim(),
      signal: abortRef.current.signal,
    });
  };

  useEffect(() => {
    if (generate.isSuccess) {
      navigate(`/generated/${generate.data.id}`);
    }
  }, [generate.isSuccess, generate.data, navigate]);

  const strings = lstr(l);

  // The playful status lines for the overlay are only fetched while a story
  // is actually being generated; the overlay shows its static message until
  // they arrive (or if the request fails).
  const progressLines = useProgressLinesQuery(
    l,
    selectedMoods,
    generate.isPending,
  );

  // One-line echo of the order, e.g. "Your B1 story is on its way — Scary ·
  // Cooking". Moods and topics keep their display capitalization: lowering
  // the case would be wrong for German nouns.
  const overlaySelectionNames = [
    ...selectedMoods.map((mood) => strings.moods[mood]),
    ...selectedTopics.map((topic) => strings.topics[topic]),
  ];
  const overlayHeadline =
    strings.generate_overlay_headline.replace("{level}", selectedLevel) +
    (overlaySelectionNames.length > 0
      ? ` — ${overlaySelectionNames.join(" · ")}`
      : "");

  const optionalLabel = "text-[11px] font-medium italic text-secondary-text";

  const renderCounter = (count: number) => (
    <span
      className={`inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold tabular-nums leading-none ${
        count > 0
          ? "bg-primary text-on-primary"
          : "bg-cream-dark text-secondary-text"
      }`}
    >
      {count}/{MAX_SELECTIONS}
    </span>
  );

  const canGenerate =
    !loggedIn ||
    (selectedLevel !== "" && !generate.isPending && !instructionsTooLong);

  // Instruction rejections and refusals to start get a specific explanation
  // above the button; editing the instructions resets the mutation so the
  // user can retry. Other errors keep the existing generic button-label
  // treatment.
  const errorMessage = (() => {
    if (!generate.isError) return null;
    const err = generate.error;
    if (err instanceof DisallowedContentError)
      return strings.generate_error_disallowed;
    if (err instanceof OffTopicInstructionsError)
      return strings.generate_error_off_topic;
    return refusalMessage(err, strings);
  })();

  return (
    <div className="flex flex-col gap-9">
      {generate.isPending && (
        <ProgressOverlay
          l={l}
          message={strings.generate_overlay_message}
          icon={<PiSparkle />}
          onCancel={cancelGenerate}
          headline={overlayHeadline}
          rotatingMessages={progressLines.data}
        />
      )}
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-literata text-[32px] font-bold leading-tight tracking-tight text-main-text md:text-4xl">
            {strings.generate_title_pre}{" "}
            <span className="text-primary">{strings.generate_title_post}</span>
          </h1>
          <p className="mt-2 max-w-lg text-secondary-text">
            {strings.generate_subtitle}
          </p>
        </div>
        <button
          type="button"
          onClick={surprise}
          className="btn-ghost shrink-0 px-3 py-2 text-sm text-primary hover:text-primary"
        >
          <PiShuffle size={17} />
          <span className="hidden sm:inline">{strings.library_surprise}</span>
        </button>
      </header>

      <EmailVerificationNotice l={l} />

      <section>
        <h2 className="eyebrow mb-3">{strings.generate_level_heading}</h2>
        <div className="grid grid-cols-3 gap-2.5 md:gap-3">
          {levels.map((level) => {
            const selected = selectedLevel === level;
            return (
              <button
                key={level}
                type="button"
                aria-pressed={selected}
                onClick={() => handleLevelSelect(level)}
                className={`relative flex flex-col items-center gap-2 rounded-2xl border-2 px-2 py-5 text-center transition-[background-color,border-color,transform] duration-150 active:scale-[0.98] sm:py-6 ${
                  selected
                    ? "border-primary bg-primary-light"
                    : "border-border bg-surface hover:bg-cream-dark"
                }`}
              >
                {selected && (
                  <PiCheckCircleFill
                    size={18}
                    className="absolute right-2 top-2 text-primary"
                  />
                )}
                <span
                  className={`font-literata text-[15px] font-bold sm:text-lg ${
                    selected ? "text-main-text" : "text-secondary-text"
                  }`}
                >
                  {strings.levels[level]}
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold sm:text-sm ${levelBadgeClasses(level)}`}
                >
                  {levelCefr[level]}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center gap-2">
          <h2 className="eyebrow">{strings.generate_mood_heading}</h2>
          {renderCounter(selectedMoods.length)}
          <span className={optionalLabel}>
            {strings.generate_topic_optional}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {moods.map((mood) => {
            const selected = selectedMoods.includes(mood);
            return (
              <button
                key={mood}
                type="button"
                aria-pressed={selected}
                disabled={!selected && selectedMoods.length >= MAX_SELECTIONS}
                onClick={() =>
                  toggleFromList(mood, selectedMoods, setSelectedMoods)
                }
                className={`${selected ? "chip-selected" : "chip-idle"} px-4 py-2`}
              >
                <span aria-hidden className="text-base leading-none opacity-80">
                  {moodIcons[mood]}
                </span>
                {strings.moods[mood]}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center gap-2">
          <h2 className="eyebrow">{strings.generate_topic_heading}</h2>
          {renderCounter(selectedTopics.length)}
          <span className={optionalLabel}>
            {strings.generate_topic_optional}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {topics.map((topic) => {
            const selected = selectedTopics.includes(topic);
            return (
              <button
                key={topic}
                type="button"
                aria-pressed={selected}
                disabled={!selected && selectedTopics.length >= MAX_SELECTIONS}
                onClick={() =>
                  toggleFromList(topic, selectedTopics, setSelectedTopics)
                }
                className={`${selected ? "chip-selected" : "chip-idle"} px-4 py-2`}
              >
                {strings.topics[topic]}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="eyebrow">{strings.generate_instructions_heading}</h2>
            <span className={optionalLabel}>
              {strings.generate_topic_optional}
            </span>
          </div>
          <span
            className={
              instructionsTooLong
                ? "text-xs font-semibold text-error"
                : "text-xs tabular-nums text-secondary-text"
            }
          >
            {instructionsLength} / {MAX_INSTRUCTIONS_CHARS}
          </span>
        </div>
        <textarea
          value={instructions}
          onChange={(e) => {
            if (generate.isError) generate.reset();
            setInstructions(e.target.value);
          }}
          rows={2}
          placeholder={strings.generate_instructions_placeholder}
          disabled={generate.isPending}
          aria-invalid={instructionsTooLong}
          className="input resize-y p-4 disabled:opacity-60"
        />
        {instructionsTooLong && (
          <div className="mt-2 text-sm font-semibold text-error">
            {strings.generate_instructions_too_long}
          </div>
        )}
        <p className="mt-2 text-sm text-secondary-text">
          {strings.generate_instructions_help}
        </p>
      </section>

      {/* Sticky so the button stays in reach: on a phone the only required
          choice (level) is at the top, two screens above the form's end. */}
      <section className="sticky bottom-0 z-10 -mx-4 flex flex-col gap-3 border-t border-border bg-cream/90 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-md">
        {errorMessage && (
          <div
            role="alert"
            className="text-center text-sm font-semibold text-error"
          >
            {errorMessage}
          </div>
        )}
        <button
          type="button"
          onClick={handleGenerate}
          className="btn-primary w-full py-4 text-lg"
          disabled={!canGenerate}
        >
          {loggedIn && generate.isIdle && selectedLevel && (
            <PiSparkle size={20} />
          )}
          {!loggedIn && strings.generate_login_prompt}
          {loggedIn &&
            generate.isIdle &&
            selectedLevel &&
            strings.generate_button}
          {loggedIn &&
            generate.isIdle &&
            !selectedLevel &&
            strings.generate_level_required}
          {loggedIn && generate.isError && strings.generate_error}
          {loggedIn &&
            !generate.isIdle &&
            !generate.isError &&
            strings.generate_in_progress}
        </button>
      </section>
    </div>
  );
}
